import React, { Fragment, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "src/data/common";
import styles from "src/components/questionnaire/questionnaire.module.scss";
import QuestionnaireSettingsFormik from "../Components/Questionnaire/QuestionnaireSettingsFormik";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import QuestionListItem from "../Components/Questionnaire/QuestionListItem";
import QuestionFormik from "../Components/Questionnaire/QuestionFormik";
import QuestionFormView from "../Components/Questionnaire/QuestionFormView";
import Api from "src/data/api";
import { v4 as uuidv4 } from "uuid";
import { showNotification } from "src/redux/notificationActions";
import { ERROR_MESSAGES, QUESTIONNAIRE_MESSAGES } from "src/constants/messages";
import { NOTIFICATION_TYPES } from "src/constants/notificationTypes";
import { NOTIFICATION_HEADERS } from "src/constants/notificationHeaders";
import QuestionnaireSettingsFormView from "../Components/Questionnaire/QuestionnaireSettingsFormView";

const USERS_LIST_KEY = ["users", "list"];

const QUESTIONS_QUERY_KEYS = {
    userQuestionnaire: (user_id) => ["questions", user_id],
    userQuestionnaireSettings: (user_id) => [
        "questionnaire",
        "settings",
        user_id,
    ],
};

const fetchQuestions = async (userId) => {
    const { data } = await Api.request({
        url: `/retailer/user/${userId}/questionnaire`,
    });
    return data.questions;
};

const saveQuestion = async (data) => {
    const result = await Api.request({
        method: "PUT",
        url: `/retailer/user/${data.userId}/questionnaire/question`,
        data: data.question,
    });

    return result.data.question;
};

const deleteQuestion = async (data) => {
    await Api.request({
        method: "DELETE",
        url: `/retailer/user/${data.userId}/questionnaire/question/${data.questionId}`,
    });
};

const updateQuestionnaireOrder = async (data) => {
    const result = await Api.request({
        method: "POST",
        url: `/retailer/user/${data.userId}/questionnaire/order`,
        data: data.data,
    });

    return result.data.questions;
};

const fetchQuestionnaireSettings = async (userId) => {
    const { data } = await Api.request({
        url: `/retailer/user/${userId}/questionnaire/settings`,
    });
    return data.questionnaireSettings;
};

const saveQuestionnaireSettings = async (data) => {
    await Api.request({
        method: "PUT",
        url: `/retailer/user/${data.userId}/questionnaire/settings`,
        data: data.settings,
    });
};

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};
const getItemStyle = (isDragging, draggableStyle) => ({
    // styles we need to apply on draggables
    ...draggableStyle,
});

const UsersQuestionnaires = () => {
    const user_id = useSelector((store) => store.user.data.user_id);
    const [currentUserId, setCurrentUserId] = useState(user_id);

    const { data: users } = useQuery({
        queryKey: USERS_LIST_KEY,
        queryFn: fetchUsers,
        placeholderData: [],
    });

    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [selectedQuestion, setSelectedQuestion] = useState(null);

    const { data: questionnaireSettings } = useQuery({
        queryKey: QUESTIONS_QUERY_KEYS.userQuestionnaireSettings(currentUserId),
        queryFn: () => fetchQuestionnaireSettings(currentUserId),
        enabled: !!currentUserId,
        placeholderData: {},
    });

    const { data: questions } = useQuery({
        queryKey: QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId),
        queryFn: () => fetchQuestions(currentUserId),
        enabled: !!currentUserId,
        placeholderData: [],
    });

    const handleAddQuestion = () => {
        setSelectedQuestion({
            question_id: uuidv4(),
            question: "",
            is_multiple_choice: false,
            choices: [],
            order:
                questions[questions.length - 1]?.order + 1 ||
                questions.length + 1,
            isNew: true,
        });
    };

    const saveQuestionMutation = useMutation(saveQuestion, {
        onSuccess: (data) => {
            setSelectedQuestion(data);
            queryClient.invalidateQueries(
                QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId)
            );

            dispatch(
                showNotification(
                    QUESTIONNAIRE_MESSAGES.SAVE_QUESTIONS_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.RETAILER_QUESTIONNAIRE
                )
            );
        },
        onError: () => {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        },
    });

    const deleteQuestionMutation = useMutation(deleteQuestion, {
        onSuccess: () => {
            setSelectedQuestion(null);
            queryClient.invalidateQueries(
                QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId)
            );
            dispatch(
                showNotification(
                    QUESTIONNAIRE_MESSAGES.DELETE_QUESTION_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.RETAILER_QUESTIONNAIRE
                )
            );
        },
        onError: () => {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        },
    });

    const questionnaireOrderMutation = useMutation(updateQuestionnaireOrder, {
        onMutate: async (data) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries([
                QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId),
            ]);

            // Snapshot the previous value
            const previousQuestions = queryClient.getQueryData(
                QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId)
            );

            queryClient.setQueryData(
                QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId),
                data.data.questions
            );

            // Return a context object with the snapshotted value
            return { previousQuestions };
        },
        onSuccess: (data) => {
            console.log(data)
            queryClient.setQueryData(
                QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId),
                data
            );

            dispatch(
                showNotification(
                    QUESTIONNAIRE_MESSAGES.REORDER_QUESTIONS_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.RETAILER_QUESTIONNAIRE
                )
            );
        },
        onError: (err, data, context) => {
            queryClient.setQueryData(
                QUESTIONS_QUERY_KEYS.userQuestionnaire(currentUserId),
                context.previousQuestions
            );

            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        },
    });

    const saveQuestionnaireSettingsMutation = useMutation(
        saveQuestionnaireSettings,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    QUESTIONS_QUERY_KEYS.userQuestionnaireSettings(
                        currentUserId
                    )
                );

                dispatch(
                    showNotification(
                        QUESTIONNAIRE_MESSAGES.SAVE_QUESTIONNAIRE_SETTINGS_SUCCESS,
                        NOTIFICATION_TYPES.SUCCESS,
                        NOTIFICATION_HEADERS.RETAILER_QUESTIONNAIRE
                    )
                );
            },
            onError: () => {
                dispatch(
                    showNotification(
                        ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                        NOTIFICATION_TYPES.ERROR,
                        NOTIFICATION_HEADERS.OOPS
                    )
                );
            },
        }
    );

    const handleSaveQuestion = (values) => {
        saveQuestionMutation.mutate({
            userId: currentUserId,
            question: values,
        });
    };

    const handleDeleteQuestion = (question) => {
        if (question.isNew) {
            setSelectedQuestion(null);
        } else {
            deleteQuestionMutation.mutate({
                userId: currentUserId,
                questionId: question.question_id,
            });
        }
    };

    const handleSaveQuestionnaireSettings = (values) => {
        saveQuestionnaireSettingsMutation.mutate({
            userId: currentUserId,
            settings: values,
        });
    };

    const onDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const items = reorder(
            questions,
            result.source.index,
            result.destination.index
        );

        questionnaireOrderMutation.mutate({
            userId: currentUserId,
            data: { questions: items },
        });
    };

    return (
        <Fragment>
            <Form.Group controlId="input-users">
                <Form.Control
                    value={currentUserId}
                    as="select"
                    custom
                    style={{ minWidth: 0, maxWidth: 400 }}
                    onChange={(e) => setCurrentUserId(e.target.value)}
                >
                    {users.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                            {user.email}
                        </option>
                    ))}
                </Form.Control>
            </Form.Group>
            <div className={styles.questionnaire}>
                <Row className={styles.questionsWrapper}>
                    <Col xs={4} className={styles.questionListCol}>
                        <div className={styles.questionListActions}>
                            <Button
                                type="button"
                                block
                                variant="secondary"
                                size="sm"
                                onClick={handleAddQuestion}
                            >
                                Add new question
                            </Button>
                        </div>
                        <QuestionnaireSettingsFormik
                            settings={questionnaireSettings}
                            saveQuestionnaireSettings={
                                handleSaveQuestionnaireSettings
                            }
                        >
                            {(props) => (
                                <QuestionnaireSettingsFormView {...props} />
                            )}
                        </QuestionnaireSettingsFormik>
                        <DragDropContext
                            className={styles.questionList}
                            onDragEnd={onDragEnd}
                        >
                            <Droppable droppableId="droppable">
                                {(provided) => (
                                    <ul
                                        {...provided.droppableProps}
                                        className={styles.questionList}
                                        ref={provided.innerRef}
                                    >
                                        {questions.map((question, index) => (
                                            <Draggable
                                                key={question.question_id}
                                                draggableId={
                                                    question.question_id
                                                }
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided
                                                                .draggableProps
                                                                .style
                                                        )}
                                                    >
                                                        <QuestionListItem
                                                            key={
                                                                question.question_id
                                                            }
                                                            question={question}
                                                            onSelect={() => {
                                                                setSelectedQuestion(
                                                                    question
                                                                );
                                                            }}
                                                            selected={
                                                                selectedQuestion?.question_id ===
                                                                question.question_id
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </ul>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {selectedQuestion?.isNew && (
                            <QuestionListItem
                                question={{ value: "Untitled" }}
                                onSelect={() => {}}
                                selected={true}
                            />
                        )}
                    </Col>
                    <Col className={styles.questionListCol} xs={8}>
                        {selectedQuestion && (
                            <QuestionFormik
                                handleSaveQuestion={handleSaveQuestion}
                                question={selectedQuestion}
                            >
                                {(props) => (
                                    <QuestionFormView
                                        handleDeleteQuestion={
                                            handleDeleteQuestion
                                        }
                                        {...props}
                                    />
                                )}
                            </QuestionFormik>
                        )}
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

export default UsersQuestionnaires;
